import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const parsePositiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const useUrlPagination = (defaultLimit = 50) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parsePositiveInteger(searchParams.get('page'), 1);
  const itemsPerPage = parsePositiveInteger(searchParams.get('limit'), defaultLimit);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    let changed = false;

    if (nextParams.get('limit') !== String(itemsPerPage)) {
      nextParams.set('limit', String(itemsPerPage));
      changed = true;
    }

    if (currentPage === 1 && nextParams.has('page')) {
      nextParams.delete('page');
      changed = true;
    } else if (currentPage > 1 && nextParams.get('page') !== String(currentPage)) {
      nextParams.set('page', String(currentPage));
      changed = true;
    }

    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [currentPage, itemsPerPage, searchParams, setSearchParams]);

  const setCurrentPage = useMemo(() => (page) => {
    const nextPage = parsePositiveInteger(page, 1);

    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('limit', String(itemsPerPage));

      if (nextPage === 1) {
        nextParams.delete('page');
      } else {
        nextParams.set('page', String(nextPage));
      }

      return nextParams;
    });
  }, [itemsPerPage, setSearchParams]);

  return { currentPage, setCurrentPage, itemsPerPage };
};

export default useUrlPagination;
